import React from 'react';
import { MoreVertical } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import type { PortfolioCardProps } from '../types';

const PortfolioCard: React.FC<PortfolioCardProps> = ({ portfolio, onView, onEdit, onDelete }) => {
  return (
    <Card className="p-6 hover:shadow-lg transition-shadow duration-200 flex flex-col h-full">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-1">{portfolio.name}</h3>
          <p className="text-sm text-gray-500">Updated {portfolio.lastUpdated}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(portfolio)}>Edit Portfolio</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDelete(portfolio)} className="text-red-600">
              Delete Portfolio
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-1">Total Portfolio Size</p>
        <p className="text-3xl font-bold text-gray-900 mb-4">
          ₹{(portfolio.totalSize || 0).toLocaleString('en-IN')}
        </p>
      </div>

      {/* Allocation Pie Chart */}
      <div className="mb-6 flex-1 flex flex-col">
        <p className="text-sm font-medium text-gray-700 mb-3">Allocation Breakdown</p>
        <div className="flex items-center justify-center">
          <svg width="160" height="160" viewBox="0 0 160 160">
            {(portfolio.categories || []).map((category, index) => {
              // Special case: if 100% allocation, draw a full circle
              if (category.percentage >= 100) {
                return (
                  <circle
                    key={category.name}
                    cx="80"
                    cy="80"
                    r="70"
                    fill={category.color}
                    opacity="0.9"
                  />
                );
              }

              const totalPercentage = portfolio.categories.slice(0, index).reduce((acc, cat) => acc + cat.percentage, 0);
              const startAngle = (totalPercentage / 100) * 360;
              const endAngle = startAngle + (category.percentage / 100) * 360;
              const largeArcFlag = category.percentage > 50 ? 1 : 0;

              const startX = 80 + 70 * Math.cos((Math.PI * startAngle) / 180);
              const startY = 80 + 70 * Math.sin((Math.PI * startAngle) / 180);
              const endX = 80 + 70 * Math.cos((Math.PI * endAngle) / 180);
              const endY = 80 + 70 * Math.sin((Math.PI * endAngle) / 180);

              return (
                <path
                  key={category.name}
                  d={`M 80 80 L ${startX} ${startY} A 70 70 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
                  fill={category.color}
                  opacity="0.9"
                />
              );
            })}
          </svg>
        </div>
      </div>

      {/* Category Tags */}
      <div className="mb-6 flex flex-wrap gap-2 min-h-[60px] items-start">
        {(portfolio.categories || []).map((category) => (
          <span
            key={category.name}
            className="px-3 py-1 text-xs font-medium rounded-full"
            style={{
              backgroundColor: `${category.color}20`,
              color: category.color
            }}
          >
            {category.name} {category.percentage}% - {category.timeframe}
          </span>
        ))}
      </div>

      <Button onClick={() => onView(portfolio)} className="w-full bg-emerald-600 hover:bg-emerald-700 mt-auto">
        View Portfolio
      </Button>
    </Card>
  );
};

export default PortfolioCard;
